import { NextRequest, NextResponse } from "next/server";
import { collectRequestContext } from "./contextCollector";
import { createSegmentEvaluator } from "./segmentEvaluator";

export interface MiddlewareConfig {
  /**
   * The API endpoint that evaluates segments based on context
   */
  apiEndpoint: string;

  /**
   * API key for authentication
   */
  apiKey: string;

  /**
   * Custom header name for segments (default: 'x-user-segments')
   */
  headerName?: string;
}

const DEFAULT_CONFIG = {
  headerName: "x-user-segments",
};

export function createMiddleware(config: MiddlewareConfig) {
  const finalConfig = { ...DEFAULT_CONFIG, ...config };
  const evaluator = createSegmentEvaluator({
    apiEndpoint: config.apiEndpoint,
    apiKey: config.apiKey,
  });

  return async function middleware(request: NextRequest) {
    console.log("🔍 Middleware triggered for:", request.url);

    try {
      // 1. Collect context from the request
      const context = collectRequestContext(request);

      // 2. Evaluate segments using the context
      const result = await evaluator.evaluateSegments(context);

      console.log("🔍 Result:", result);

      // 3. Create a single response
      const response = NextResponse.next();

      // 4. Set the header by joining the segment strings directly
      const segments = result.segments?.join(",") || "";
      response.headers.set(finalConfig.headerName, segments);

      // 5. Return the response
      return response;
    } catch (error) {
      // In case of any error from the above steps, log it and return
      // a clean response with an empty segment header.
      console.error("Personalization middleware error:", error);
      const response = NextResponse.next();
      response.headers.set(finalConfig.headerName, "");
      return response;
    }
  };
}

// Export a default middleware creator for convenience
export const createPersonalizationMiddleware = createMiddleware;

export const config = {
  matcher: ["/((?!_next/static|_next/image|favicon.ico|public/).*)"],
};
